/**
 * Service worker registration helper.
 *
 * - Only registers in production builds (CRA exposes NODE_ENV).
 * - Listens for updates and exposes a `window.__rotaApplyUpdate()` callback
 *   so the UI can offer a "Reload to update" toast when a new SW is waiting.
 * - Silent failure: if registration throws (e.g. on an http://… preview)
 *   we log a single warning and move on.
 */
export function registerServiceWorker({ onUpdateAvailable } = {}) {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Don't enable the SW in dev — CRA's hot-reload doesn't play well with it.
  if (process.env.NODE_ENV !== 'production') return;
  // The SW only works on a secure origin. localhost is also allowed.
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;

  const start = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        if (!reg) return;

        // A new SW was found and is installing.
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              // There's an old SW still controlling the page → new version waiting.
              if (typeof onUpdateAvailable === 'function') onUpdateAvailable(reg);
              window.__rotaApplyUpdate = () => {
                sw.postMessage('SKIP_WAITING');
              };
            }
          });
        });
      })
      .catch((err) => {
        console.warn('Service worker registration failed:', err);
      });

    // When a new SW takes control, reload so the user sees the new bundle.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  };

  // Register even when the `load` event has ALREADY fired (installability
  // must never depend on script timing).
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);
}
